import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { merge, Observable, scan, startWith, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { generalConfigFromGlobal } from '../../shared/workspace-element-context';
import { ScreenVideoListService } from './screen-video-list.service';
import { ScreenVideoDialogComponent } from './screen-video-dialog/screen-video-dialog.component';
import { ScreenVideo } from './screen-video';
import { UserService } from '../user.service';
import { ScreenVideoDeleteComponent } from './screen-video-delete.component';

interface ScreenDocument extends Document {
  pictureInPictureElement: any;
  requestPictureInPicture(): any;
  exitPictureInPicture: () => Promise<any>;
}

@Component({
    selector: 'gtw-screen-video-list',
    templateUrl: './screen-video-list.component.html',
    styleUrls: ['./screen-video-list.component.scss'],
    providers: [ScreenVideoListService],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScreenVideoListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  dialogRef: MatDialogRef<ScreenVideoDialogComponent, any> | undefined;
  videoList$!: Observable<any>;
  merged$!: Observable<any>;
  deleteSubject = new Subject();
  isLoading = false;
  durationInSeconds = 5;

  @Output()
  updateScreenVideoEvent = new EventEmitter();

  @Input('refresh')
  set refresh(value: boolean) {
    if (value) {
      this.getAllVideos();
    }
  }

  constructor(
    public gtwGlobal: GlobalService,
    public gtwWorkspace: WorkspaceService,
    public gtwApp: AppService,
    private screenVideoListService: ScreenVideoListService,
    public matDialog: MatDialog,
    private userService: UserService,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    if (!isNaN(this.clientKeyNum) && !isNaN(this.screenKeyNum)) {
      this.getAllVideos();
    }
  }

  private activeScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen || {};
  }

  private getBaseUrl(): string {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    return String(gc.base_url ?? gc.api ?? '');
  }

  get screenKeyNum(): number {
    const a = this.activeScreen();
    const raw = a.link ?? a.screenKey ?? a.screen_key;
    return parseInt(String(raw), 10);
  }

  get clientKeyNum(): number {
    const raw =
      this.gtwGlobal.global?.globalParams?.client_key ?? this.gtwApp.USER_SETTINGS?.user?.client_key;
    return parseInt(String(raw), 10);
  }

  private getAllVideos() {
    this.isLoading = true;
    this.videoList$ = this.screenVideoListService.getAllVideos(
      this.getBaseUrl(),
      this.clientKeyNum,
      this.screenKeyNum
    );
    this.merged$ = merge(this.videoList$, this.deleteSubject).pipe(
      startWith({}),
      scan((videoList, data) => {
        if (data && Object.keys(data).length > 0) {
          this.isLoading = false;
        }
        if (data.operation && data.operation == 'delete') {
          const index = videoList.data.findIndex((elt: ScreenVideo) => elt.video_id === data.id);
          videoList.data.splice(index, 1);
          return videoList;
        }
        return data;
      })
    );
  }

  async openVideoDialog(video: ScreenVideo) {
    this.matDialog.closeAll();
    this.dialogRef?.close();

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { ...video, ...{ baseURL: this.getBaseUrl() } };
    dialogConfig.panelClass = 'remove-dialog-css';
    dialogConfig.hasBackdrop = false;
    dialogConfig.width = '30vw';
    (dialogConfig.position = { right: '0px', top: '55vh' }),
      (dialogConfig.id = video.video_id.toString());

    if ((document as ScreenDocument).pictureInPictureElement) {
      await (document as ScreenDocument).exitPictureInPicture();
    }

    this.dialogRef = undefined;
    this.dialogRef = this.matDialog.open(ScreenVideoDialogComponent, dialogConfig);
    const event = new Event('playvideo');
    document.dispatchEvent(event);
  }

  updateScreenVideo(video: ScreenVideo): void {
    this.updateScreenVideoEvent.emit(video);
  }

  deleteScreenVideo(id: number): void {
    this.isLoading = true;
    this.screenVideoListService.deleteScreenVideo(this.getBaseUrl(), this.clientKeyNum, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
          this.deleteSubject.next({ operation: 'delete', id: id });
          this.openSnackBar('Video deleted Successfully !!');
        } else {
          this.openSnackBar('Video delete failed: ' + res.errorMessage);
        }
      },
      (error) => {
        this.openSnackBar('Video delete Failed');
        console.log(error);
      }
    );
  }

  isValidUser(sso: string) {
    if (this.userService.isValidUser(['admin']) && this.userService.getUser()?.sso == sso) {
      return true;
    }
    if (this.userService.isValidUser(['super admin', 'developer'])) {
      return true;
    }

    return false;
  }

  private openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: this.durationInSeconds * 1000,
    });
  }

  confirmDelete(id: number): void {
    const dialogRef = this.matDialog.open(ScreenVideoDeleteComponent);

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      console.log(`Dialog result: ${result}`);
      if (result) {
        this.deleteScreenVideo(id);
      }
    });
  }
}
